import React, { useState } from 'react';
import { ChevronDown, ChevronRight, ArrowDownUp } from 'lucide-react';
import { OrderTimelineTransactionDto } from '../../../../../types/orderTimeline';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface TimelineStockTransactionsProps {
  transactions: OrderTimelineTransactionDto[];
}

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export const TimelineStockTransactions: React.FC<TimelineStockTransactionsProps> = ({ transactions }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <ArrowDownUp className="w-4 h-4" />
        {t('manager.modules.orderTimeline.stockTransactions.title', { '0': transactions.length }, `Lagertransaksjoner (${transactions.length})`)}
      </button>

      {expanded && (
        <div className="overflow-x-auto">
          {transactions.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-4 bg-white rounded-lg border border-gray-200">
              {t('manager.modules.orderTimeline.stockTransactions.empty', {}, 'Ingen lagertransaksjoner registrert for denne ordren')}
            </div>
          ) : (
            <table className="w-full text-xs border-collapse bg-white rounded-lg border border-gray-200">
              <thead>
                <tr className="bg-gray-50 text-gray-600">
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('manager.modules.orderTimeline.stockTransactions.cols.id', {}, 'ID')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('common.fields.date', {}, 'Dato')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('common.fields.type', {}, 'Type')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('manager.modules.orderTimeline.orderLines.cols.itemNo', {}, 'Varenr')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('warehouse.general.bin', {}, 'Lokasjon')}</th>
                  <th className="text-right px-2 py-1.5 font-medium border-b border-gray-200">{t('manager.modules.orderTimeline.stockTransactions.cols.amount', {}, 'Mengde')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('warehouse.general.operator', {}, 'Operatør')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('manager.modules.orderTimeline.stockTransactions.cols.order', {}, 'Ordre')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('manager.modules.orderTimeline.orderLines.cols.pList', {}, 'PList')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('warehouse.general.warehouse', {}, 'Lager')}</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-2 py-1.5 text-gray-400">{t.id}</td>
                    <td className="px-2 py-1.5 text-gray-600">{formatDateTime(t.transDate)}</td>
                    <td className="px-2 py-1.5">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700">
                        {t.transTypeText}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 font-mono font-medium text-gray-800">{t.itemNo}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-gray-600">{t.binCode}</td>
                    <td className={`px-2 py-1.5 text-right font-medium ${t.quantity < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {t.quantity > 0 ? '+' : ''}{t.quantity}
                    </td>
                    <td className="px-2 py-1.5 text-gray-600">{t.operatorID || '–'}</td>
                    <td className="px-2 py-1.5 text-gray-600">
                      {t.orderNo ? `${t.orderNo}-${t.lineNo}` : '–'}
                    </td>
                    <td className="px-2 py-1.5 text-gray-600">
                      {t.pListID ? `${t.pListID}-${t.pListLineNo}` : '–'}
                    </td>
                    <td className="px-2 py-1.5 text-gray-500">{t.warehouseID || '–'}</td>
                    <td className="px-2 py-1.5 text-gray-500 max-w-[200px] truncate">{t.reasonDescription || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};
